import { useState, useEffect, useRef } from "react";
import { useStableCallback } from "@/hooks/useStableCallback";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, X, Building2, AlertCircle, BarChart3, ImageIcon, Upload } from "lucide-react";
import { BACKEND_URL, API } from "@/apiConfig";

// Convert local/network paths to proxy URL for browser display
export function toImageSrc(path) {
  if (!path) return null;
  // Already a HTTP URL - use as-is
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  // Relative API path from upload (e.g. /api/images/xxx.jpg)
  if (path.startsWith("/api/")) {
    return `${BACKEND_URL}${path}`;
  }
  // Local or network path - proxy through backend
  return `${BACKEND_URL}/api/image-proxy?path=${encodeURIComponent(path)}`;
}

const getErrorMessage = (error, fallback = "Operation failed") => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || d).join(", ");
  if (typeof detail === 'string') return detail;
  return fallback;
};

const INITIAL_FORM = {
  sku: "", alt_skus: [], name: "", cost_price: "", retail_price: "", gp_percent: "", units_per_case: "",
  tax_1: false, tax_2: false, deposit: false, foodstamp: false,
  category: "", sub_category: "", group: "", mfg: "", vendor_ids: [], scan_data_mfg: [],
  upc: "", sd_category: "", sd_manufacturer: "", sd_uom: "", sd_consumer_units: "",
  age_18_plus: false, age_21_plus: false, ask_for_price: false, fluid_oz: "",
  image_url: "", stock_quantity: "", min_stock_level: "", cost_updated_at: "", price_updated_at: ""
};

export default function ProductForm({ 
  open, onOpenChange, editingProduct, 
  categories, subCategories, manufacturers, groups, vendors, enabledScanDataMfgs,
  onSave, onShowVendorDialog, onDuplicate,
  onFetchCategories, onFetchSubCategories, onFetchManufacturers, onFetchGroups, onRefreshScanDataMfgs
}) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [skuError, setSkuError] = useState("");
  const [altSkuErrors, setAltSkuErrors] = useState({});
  const [newAltSku, setNewAltSku] = useState("");
  const [newAltSkuError, setNewAltSkuError] = useState("");
  const skuCheckTimer = useRef(null);
  const altSkuCheckTimer = useRef(null);
  const isDuplicating = useRef(false);
  const [isDuplicated, setIsDuplicated] = useState(false);
  const [selectedScanDataMfg, setSelectedScanDataMfg] = useState("");
  const [showNewScanDataTag, setShowNewScanDataTag] = useState(false);
  const [newScanDataTagName, setNewScanDataTagName] = useState("");
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewSubCategory, setShowNewSubCategory] = useState(false);
  const [showNewManufacturer, setShowNewManufacturer] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryGpMargin, setNewCategoryGpMargin] = useState("35");
  const [newSubCategoryName, setNewSubCategoryName] = useState("");
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [userEnteredCost, setUserEnteredCost] = useState(false);
  const [centralBlocked, setCentralBlocked] = useState(false);

  // Catalog data rule — Central is the only writer. Poll reachability continuously
  // (not just while open) so the Add Product trigger button is disabled up front too.
  useEffect(() => {
    const checkStatus = () => {
      const token = localStorage.getItem("token");
      axios.get(`${API}/products/central-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setCentralBlocked(r.data.configured && !r.data.reachable))
        .catch(() => setCentralBlocked(false));
    };
    checkStatus();
    const interval = setInterval(checkStatus, 20000);
    return () => clearInterval(interval);
  }, []);

  // GP% Calculations
  const calculateGpFromPrices = (cost, retail) => {
    const costNum = parseFloat(cost); const retailNum = parseFloat(retail);
    if (retailNum > 0 && costNum >= 0) return (((retailNum - costNum) / retailNum) * 100).toFixed(1);
    return null;
  };
  const calculateRetailFromCostAndGp = (cost, gpPercent) => {
    const costNum = parseFloat(cost); const gpNum = parseFloat(gpPercent);
    if (costNum > 0 && gpNum >= 0 && gpNum < 100) return (costNum / (1 - gpNum / 100)).toFixed(2);
    return null;
  };
  const calculateCostFromGp = (retail, gpPercent) => {
    const retailNum = parseFloat(retail); const gpNum = parseFloat(gpPercent);
    if (retailNum > 0 && gpNum >= 0 && gpNum < 100) return (retailNum * (1 - gpNum / 100)).toFixed(2);
    return null;
  };

  // Populate form when editing
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  useEffect(() => {
    if (editingProduct && open) {
      let gpPercent = "";
      if (editingProduct.cost_price && (editingProduct.retail_price || editingProduct.price)) {
        gpPercent = calculateGpFromPrices(editingProduct.cost_price, editingProduct.retail_price || editingProduct.price) || "";
      } else if (editingProduct.category) {
        const cat = categoriesRef.current.find(c => c.id === editingProduct.category);
        gpPercent = cat?.gp_margin?.toString() || "";
      }
      setFormData({
        sku: editingProduct.sku || "", alt_skus: editingProduct.alt_skus || [],
        name: editingProduct.name || "",
        cost_price: editingProduct.cost_price?.toString() || "",
        units_per_case: editingProduct.units_per_case?.toString() || "",
        retail_price: editingProduct.retail_price?.toString() || editingProduct.price?.toString() || "",
        gp_percent: gpPercent,
        tax_1: editingProduct.tax_1 || false, tax_2: editingProduct.tax_2 || false,
        deposit: editingProduct.deposit || false, foodstamp: editingProduct.foodstamp || false,
        category: editingProduct.category || "", sub_category: editingProduct.sub_category || "",
        group: editingProduct.group || "", mfg: editingProduct.mfg || "",
        vendor_ids: editingProduct.vendor_ids || [], scan_data_mfg: editingProduct.scan_data_mfg || [],
        upc: editingProduct.upc || "", sd_category: editingProduct.sd_category || "", sd_manufacturer: editingProduct.sd_manufacturer || "",
        sd_uom: editingProduct.sd_uom || "", sd_consumer_units: editingProduct.sd_consumer_units ?? "",
        age_18_plus: editingProduct.age_18_plus || false, age_21_plus: editingProduct.age_21_plus || false,
        ask_for_price: editingProduct.ask_for_price || false,
        fluid_oz: editingProduct.fluid_oz?.toString() || "",
        image_url: editingProduct.image_url || "",
        stock_quantity: editingProduct.stock_quantity?.toString() || "",
        min_stock_level: editingProduct.min_stock_level?.toString() || "",
        cost_updated_at: editingProduct.cost_updated_at || "",
        price_updated_at: editingProduct.price_updated_at || ""
      });
    } else if (!editingProduct && open) {
      if (isDuplicating.current) {
        isDuplicating.current = false;
        // Don't reset — form data was already set by handleSubmit
      } else {
        resetForm();
      }
    }
  }, [editingProduct, open]);

  // Fetch subcategories when category changes (stable wrapper: parent callback identity may change per render)
  const fetchSubCategories = useStableCallback((cat) => { if (onFetchSubCategories) onFetchSubCategories(cat); });
  useEffect(() => {
    if (formData.category) fetchSubCategories(formData.category);
  }, [formData.category, fetchSubCategories]);

  const resetForm = () => {
    setFormData(INITIAL_FORM); setSkuError(""); centralConflictSkuRef.current = ""; setAltSkuErrors({});
    setNewAltSku(""); setNewAltSkuError(""); setSelectedScanDataMfg("");
    setShowNewCategory(false); setShowNewSubCategory(false);
    setShowNewManufacturer(false); setShowNewGroup(false);
    setNewCategoryName(""); setNewSubCategoryName("");
    setNewManufacturerName(""); setNewGroupName("");
    setUserEnteredCost(false); setIsDuplicated(false);
  };

  // Guards against a stale local-only validateSku() call (fired by the SKU field's onBlur
  // when Save is clicked) clobbering a just-set Central sku_conflict error — see handleSubmit.
  const centralConflictSkuRef = useRef("");

  // SKU validation
  const validateSku = async (sku, isAltSku = false, index = null) => {
    if (!sku.trim()) { if (isAltSku) setAltSkuErrors(p => ({...p, [index]: ""})); else setSkuError(""); return null; }
    try {
      const excludeId = editingProduct?.id || null;
      const url = excludeId ? `${API}/products/validate-sku/${sku}?exclude_product_id=${excludeId}` : `${API}/products/validate-sku/${sku}`;
      const response = await axios.get(url);
      if (response.data.exists) {
        const msg = `SKU "${sku}" already exists in product "${response.data.product_name}"`;
        if (isAltSku) setAltSkuErrors(p => ({...p, [index]: msg})); else setSkuError(msg);
        return msg;
      } else {
        if (isAltSku) setAltSkuErrors(p => ({...p, [index]: ""}));
        else if (sku !== centralConflictSkuRef.current) setSkuError("");
        return null;
      }
    } catch (error) { return null; }
  };

  const checkLocalDuplicates = (sku, isAltSku = false, index = null) => {
    if (!sku.trim()) return true;
    if (isAltSku && sku === formData.sku) { setAltSkuErrors(p => ({...p, [index]: "Alt-SKU cannot be same as main SKU"})); return false; }
    if (isAltSku) { const others = formData.alt_skus.filter((_, i) => i !== index); if (others.includes(sku)) { setAltSkuErrors(p => ({...p, [index]: "Duplicate Alt-SKU"})); return false; } }
    if (!isAltSku && formData.alt_skus.includes(sku)) { setSkuError("Main SKU cannot be same as any Alt-SKU"); return false; }
    return true;
  };

  const handleSkuBlur = async (e) => {
    const sku = e.target.value;
    if (checkLocalDuplicates(sku, false)) {
      const err = await validateSku(sku, false);
      if (!err) await lookupSharedItem(sku);
    }
  };
  const handleAltSkuBlur = async (index) => { const sku = formData.alt_skus[index]; if (checkLocalDuplicates(sku, true, index)) await validateSku(sku, true, index); };

  // Shared cross-store item catalog — only for brand-new products (not editing an
  // existing one), and only fills in Name/Units-per-case if the user hasn't typed
  // anything there yet. Best-effort: silently no-ops if Central is unreachable.
  const lookupSharedItem = async (sku) => {
    if (!sku.trim() || editingProduct) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/products/item-catalog-lookup/${sku}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.found) {
        setFormData(prev => ({
          ...prev,
          name: prev.name.trim() ? prev.name : (response.data.item_name || "").toUpperCase(),
          units_per_case: (prev.units_per_case === "" && response.data.units_per_case != null) ? response.data.units_per_case : prev.units_per_case,
        }));
      }
    } catch (error) { /* best-effort, ignore */ }
  };

  const addAltSku = async () => {
    if (!newAltSku.trim()) return;
    if (newAltSku === formData.sku) { setNewAltSkuError("Alt-SKU cannot be same as main SKU"); return; }
    if (formData.alt_skus.includes(newAltSku)) { setNewAltSkuError("This Alt-SKU already exists"); return; }
    const error = await validateSku(newAltSku, true, formData.alt_skus.length);
    if (!error) {
      setFormData(p => ({...p, alt_skus: [...p.alt_skus, newAltSku]}));
      setNewAltSku(""); setNewAltSkuError("");
      setAltSkuErrors(p => { const n = {...p}; delete n[formData.alt_skus.length]; return n; });
    } else { setNewAltSkuError(error); setAltSkuErrors(p => { const n = {...p}; delete n[formData.alt_skus.length]; return n; }); }
  };

  const removeAltSku = (index) => {
    setFormData(p => ({...p, alt_skus: p.alt_skus.filter((_, i) => i !== index)}));
    setAltSkuErrors(p => { const n = {...p}; delete n[index]; return n; });
  };

  const hasSkuError = !!skuError;

  // Category change with auto-fill
  const handleCategoryChange = (categoryId) => {
    const selectedCategory = categories.find(c => c.id === categoryId);
    setFormData(prev => {
      const updated = { ...prev, category: categoryId, sub_category: "" };
      // Only update GP% and cost for NEW products when user hasn't manually entered cost
      if (!editingProduct && !userEnteredCost && selectedCategory?.gp_margin) {
        updated.gp_percent = selectedCategory.gp_margin.toString();
        if (prev.retail_price) { const c = calculateCostFromGp(prev.retail_price, selectedCategory.gp_margin); if (c) updated.cost_price = c; }
      }
      if (selectedCategory) {
        updated.tax_1 = selectedCategory.tax_1 || false; updated.tax_2 = selectedCategory.tax_2 || false;
        updated.foodstamp = selectedCategory.foodstamp || false; updated.deposit = selectedCategory.deposit || false;
        updated.age_18_plus = selectedCategory.age_18_plus || false; updated.age_21_plus = selectedCategory.age_21_plus || false;
      }
      return updated;
    });
  };

  // Auto-decimal: strip non-digits, treat as cents → "299" becomes "2.99"
  const autoDecimal = (raw) => {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return '';
    return (parseInt(digits, 10) / 100).toFixed(2);
  };

  // 1. Cost changes → recalculate Retail based on GP% (if GP% exists), else recalculate GP% from Cost & Retail
  const handleCostChange = (value) => {
    if (value) setUserEnteredCost(true);
    setFormData(prev => {
      const u = {...prev, cost_price: value};
      if (value && prev.gp_percent) {
        const r = calculateRetailFromCostAndGp(value, prev.gp_percent);
        if (r) u.retail_price = r;
      } else if (prev.retail_price && value) {
        const g = calculateGpFromPrices(value, prev.retail_price);
        if (g !== null) u.gp_percent = g;
      }
      return u;
    });
  };
  // 2. Retail changes → recalculate GP% based on Cost and Retail
  const handleRetailChange = (value) => {
    setFormData(prev => {
      const u = {...prev, retail_price: value};
      if (prev.cost_price && value) {
        const g = calculateGpFromPrices(prev.cost_price, value);
        if (g !== null) u.gp_percent = g;
      }
      return u;
    });
  };
  // 3. GP% changes → recalculate Retail based on Cost and GP%
  const handleGpChange = (value) => {
    setFormData(prev => {
      const u = {...prev, gp_percent: value};
      if (prev.cost_price && value) {
        const r = calculateRetailFromCostAndGp(prev.cost_price, value);
        if (r) u.retail_price = r;
      }
      return u;
    });
  };

  // Inline add handlers
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    const token = localStorage.getItem("token");
    try { const r = await axios.post(`${API}/categories`, { name: newCategoryName, gp_margin: parseFloat(newCategoryGpMargin) || 35 }, { headers: { Authorization: `Bearer ${token}` } });
      setFormData(p => ({...p, category: r.data.id})); setNewCategoryName(""); setNewCategoryGpMargin("35"); setShowNewCategory(false);
      if (onFetchCategories) onFetchCategories(); toast.success("Category added");
    } catch (error) { toast.error(getErrorMessage(error, "Failed to add category")); }
  };
  const handleAddSubCategory = async () => {
    if (!newSubCategoryName.trim() || !formData.category) return;
    const token = localStorage.getItem("token");
    try { const r = await axios.post(`${API}/sub-categories`, { name: newSubCategoryName, category_id: formData.category }, { headers: { Authorization: `Bearer ${token}` } });
      setFormData(p => ({...p, sub_category: r.data.id})); setNewSubCategoryName(""); setShowNewSubCategory(false);
      if (onFetchSubCategories) onFetchSubCategories(formData.category); toast.success("Sub-category added");
    } catch (error) { toast.error(getErrorMessage(error, "Failed to add sub-category")); }
  };
  const handleAddManufacturer = async () => {
    if (!newManufacturerName.trim()) return;
    const token = localStorage.getItem("token");
    try { const r = await axios.post(`${API}/manufacturers`, { name: newManufacturerName }, { headers: { Authorization: `Bearer ${token}` } });
      setFormData(p => ({...p, mfg: r.data.id})); setNewManufacturerName(""); setShowNewManufacturer(false);
      if (onFetchManufacturers) onFetchManufacturers(); toast.success("Manufacturer added");
    } catch (error) { toast.error(getErrorMessage(error, "Failed to add manufacturer")); }
  };
  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;
    const token = localStorage.getItem("token");
    try { const r = await axios.post(`${API}/groups`, { name: newGroupName }, { headers: { Authorization: `Bearer ${token}` } });
      setFormData(p => ({...p, group: r.data.id})); setNewGroupName(""); setShowNewGroup(false);
      if (onFetchGroups) onFetchGroups(); toast.success("Group added");
    } catch (error) { toast.error(getErrorMessage(error, "Failed to add group")); }
  };

  const toggleVendor = (vendorId) => { setFormData(p => ({...p, vendor_ids: p.vendor_ids.includes(vendorId) ? p.vendor_ids.filter(id => id !== vendorId) : [...p.vendor_ids, vendorId]})); };

  const filteredSubCategories = subCategories.filter(sc => sc.category_id === formData.category);
  const selectedCategory = categories.find(c => c.id === formData.category);
  const fluidLimitEnabled = !!selectedCategory?.fluid_limit_enabled;

  const handleSubmit = async (e, saveAndDuplicate = false) => {
    e.preventDefault();
    if (skuError || newAltSkuError || Object.values(altSkuErrors).some(err => err)) { toast.error("Please fix SKU errors before saving"); return; }
    const token = localStorage.getItem("token");
    try {
      const submitData = {
        ...formData,
        cost_price: parseFloat(formData.cost_price) || 0,
        units_per_case: formData.units_per_case === "" ? null : parseInt(formData.units_per_case),
        retail_price: parseFloat(formData.retail_price) || 0,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        min_stock_level: parseInt(formData.min_stock_level) || 0,
        gp_percent: formData.gp_percent ? parseFloat(formData.gp_percent) : null,
        fluid_oz: fluidLimitEnabled && formData.fluid_oz ? parseFloat(formData.fluid_oz) : null,
        cost_updated_at: formData.cost_updated_at || null,
        price_updated_at: formData.price_updated_at || null,
        image_url: formData.image_url || null,
        sd_consumer_units: formData.sd_consumer_units === "" ? null : parseInt(formData.sd_consumer_units),
      };
      if (editingProduct) { await axios.put(`${API}/products/${editingProduct.id}`, submitData, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Product updated successfully"); }
      else { await axios.post(`${API}/products`, submitData, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Product added successfully"); }

      if (saveAndDuplicate) {
        // Keep form data but clear SKU, alt_skus, stock, and errors for a new product
        const dupeData = { ...formData, sku: "", alt_skus: [], stock_quantity: "", min_stock_level: "" };
        if (editingProduct && onDuplicate) {
          // Set flag so useEffect doesn't reset the form
          isDuplicating.current = true;
          onDuplicate();
        }
        setFormData(dupeData);
        setSkuError(""); centralConflictSkuRef.current = ""; setAltSkuErrors({}); setNewAltSku(""); setNewAltSkuError("");
        setUserEnteredCost(true);
        setIsDuplicated(true);
        if (onSave) onSave();
      } else {
        resetForm();
        onOpenChange(false);
        if (onSave) onSave();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 503) {
        toast.error("Can't reach Central — check your internet connection and try again.");
      } else if (error.response?.status === 409 && detail && typeof detail === "object" && detail.error === "sku_conflict") {
        // Guided fix-it: this SKU already belongs to another product in Central that this
        // PC hasn't synced locally yet (legacy pre-Central data) — same inline SKU error UI
        // as the live local duplicate check, just with the richer Central-sourced message.
        setSkuError(`SKU "${detail.sku}" is already used by "${detail.conflicting_product_name || "another product"}" — pick a different SKU.`);
        centralConflictSkuRef.current = detail.sku;
        toast.error("That SKU is already in use — see the highlighted field.");
      } else {
        toast.error(getErrorMessage(error, "Operation failed"));
      }
    }
  };

  return (
          <Dialog open={open} onOpenChange={(val) => {
            onOpenChange(val);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm disabled:opacity-40" disabled={centralBlocked} data-testid="add-product-button" onClick={() => { if (editingProduct) resetForm(); onOpenChange(true); }}>
                <Plus className="w-4 h-4 mr-1" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl p-0">
              <div className="px-5 pt-4 pb-2 border-b border-zinc-100">
                <DialogTitle className="text-lg font-bold text-zinc-900">
                  {editingProduct ? "Edit Product" : "Add New Product"}
                </DialogTitle>
              </div>
              {centralBlocked && (
                <div className="px-5 pt-3" data-testid="central-unreachable-banner">
                  <div className="flex items-center gap-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    Can't reach Central — reconnect to the internet to add, edit, or delete products.
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="px-5 pb-4 pt-3 space-y-2.5">

                {/* Row 1: SKU (half) | Toggles (half) */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-zinc-600">SKU *</Label>
                      <div className="flex items-center gap-1 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full text-xs">
                        <Switch id="ask_for_price" checked={formData.ask_for_price} onCheckedChange={(c) => setFormData({ ...formData, ask_for_price: c })} className="scale-75" data-testid="ask-for-price-toggle" tabIndex={-1} />
                        <Label htmlFor="ask_for_price" className="text-[11px] cursor-pointer font-medium text-amber-700">Ask$</Label>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-0.5">
                      <Input
                        value={formData.sku}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, sku: val }); setSkuError(""); centralConflictSkuRef.current = "";
                          if (isDuplicated && val) setIsDuplicated(false);
                          // Debounced real-time validation
                          if (skuCheckTimer.current) clearTimeout(skuCheckTimer.current);
                          if (val.trim()) {
                            skuCheckTimer.current = setTimeout(() => {
                              if (checkLocalDuplicates(val, false)) validateSku(val, false);
                            }, 400);
                          }
                        }}
                        onKeyDown={(e) => {
                          // Barcode scanners send digits + Enter. Prevent Enter
                          // from submitting the form so the user gets to review
                          // Product Name (and rest of the fields) before saving.
                          // Instead, move focus to Product Name.
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const nameEl = document.querySelector('[data-testid="product-name-input"]');
                            if (nameEl) { nameEl.focus(); nameEl.select?.(); }
                          }
                        }}
                        onBlur={handleSkuBlur}
                        required
                        className={`h-8 text-sm font-mono flex-1 ${skuError ? "border-red-400 bg-red-50" : isDuplicated ? "border-red-400 ring-1 ring-red-300 sku-duplicate-placeholder" : ""}`}
                        placeholder={isDuplicated ? "ENTER NEW SKU HERE" : "Scan barcode"}
                        data-testid="product-sku-input"
                      />
                      {formData.sku && (
                        <Button type="button" variant="outline" className="h-8 px-2 text-xs" onClick={() => { setFormData({ ...formData, sku: "" }); setSkuError(""); centralConflictSkuRef.current = ""; }}>Clear</Button>
                      )}
                    </div>
                    {skuError && <p className="text-red-500 text-[10px] mt-0.5" data-testid="sku-error">{skuError}</p>}
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 content-end">
                    <div className="flex items-center gap-1 bg-zinc-50 px-2 py-1 rounded text-xs">
                      <Switch id="tax_1" checked={formData.tax_1} onCheckedChange={(c) => setFormData({ ...formData, tax_1: c })} className="scale-75" tabIndex={-1} />
                      <Label htmlFor="tax_1" className="text-[11px] cursor-pointer">Tax1</Label>
                    </div>
                    <div className="flex items-center gap-1 bg-zinc-50 px-2 py-1 rounded text-xs">
                      <Switch id="tax_2" checked={formData.tax_2} onCheckedChange={(c) => setFormData({ ...formData, tax_2: c })} className="scale-75" tabIndex={-1} />
                      <Label htmlFor="tax_2" className="text-[11px] cursor-pointer">Tax2</Label>
                    </div>
                    <div className="flex items-center gap-1 bg-zinc-50 px-2 py-1 rounded text-xs">
                      <Switch id="deposit" checked={formData.deposit} onCheckedChange={(c) => setFormData({ ...formData, deposit: c })} className="scale-75" tabIndex={-1} />
                      <Label htmlFor="deposit" className="text-[11px] cursor-pointer">Dep</Label>
                    </div>
                    <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded text-xs">
                      <Switch id="foodstamp" checked={formData.foodstamp} onCheckedChange={(c) => setFormData({ ...formData, foodstamp: c })} className="scale-75" tabIndex={-1} />
                      <Label htmlFor="foodstamp" className="text-[11px] cursor-pointer">FS</Label>
                    </div>
                    <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded text-xs">
                      <Switch id="age_18" checked={formData.age_18_plus} onCheckedChange={(c) => setFormData({ ...formData, age_18_plus: c })} className="scale-75" tabIndex={-1} />
                      <Label htmlFor="age_18" className="text-[11px] cursor-pointer">18+</Label>
                    </div>
                    <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded text-xs">
                      <Switch id="age_21" checked={formData.age_21_plus} onCheckedChange={(c) => setFormData({ ...formData, age_21_plus: c })} className="scale-75" tabIndex={-1} />
                      <Label htmlFor="age_21" className="text-[11px] cursor-pointer">21+</Label>
                    </div>
                  </div>
                </div>

                {/* Row 2: Name */}
                <div>
                  <Label className="text-xs font-medium text-zinc-600">Product Name * <span className="text-zinc-400 font-normal">({formData.name?.length || 0}/40)</span></Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => {
                      let val = e.target.value.slice(0, 40).toUpperCase();
                      setFormData({ ...formData, name: val });
                    }}
                    maxLength={40} required disabled={hasSkuError}
                    className="h-8 text-sm mt-0.5"
                    placeholder="Product name"
                    data-testid="product-name-input"
                  />
                </div>

                <fieldset disabled={hasSkuError} className={hasSkuError ? "opacity-40 pointer-events-none space-y-2.5" : "space-y-2.5"}>

                {/* Row 3: Cost | Retail | GP% | Units per Case */}
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Cost</Label>
                    <div className="relative mt-0.5">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                      <Input type="text" inputMode="decimal" value={formData.cost_price} onChange={(e) => handleCostChange(autoDecimal(e.target.value))} placeholder="0.00" className="h-8 text-sm pl-6 font-mono" data-testid="product-cost-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Retail *</Label>
                    <div className="relative mt-0.5">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                      <Input type="text" inputMode="decimal" value={formData.retail_price} onChange={(e) => handleRetailChange(autoDecimal(e.target.value))} required className="h-8 text-sm pl-6 font-mono" data-testid="product-price-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">GP%</Label>
                    <Input type="number" step="0.1" value={formData.gp_percent} onChange={(e) => handleGpChange(e.target.value)} placeholder="35" className="h-8 text-sm mt-0.5 bg-blue-50/50" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Unit per Case</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={formData.units_per_case}
                      onChange={(e) => setFormData({ ...formData, units_per_case: e.target.value === "" ? "" : parseInt(e.target.value) })}
                      placeholder="e.g. 24"
                      className="h-8 text-sm mt-0.5"
                      data-testid="product-units-per-case-input"
                    />
                    {formData.units_per_case && parseFloat(formData.cost_price) > 0 && (
                      <p className="text-[10px] text-zinc-500 mt-0.5" data-testid="product-case-cost">
                        Case cost ${(parseFloat(formData.cost_price) * parseInt(formData.units_per_case)).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 4: Category | Sub-Category */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Category *</Label>
                    {!showNewCategory ? (
                      <select value={formData.category} onChange={(e) => { if (e.target.value === "__new__") { setShowNewCategory(true); } else { handleCategoryChange(e.target.value); } }}
                        className="w-full h-8 text-sm border border-zinc-200 rounded-md px-2 mt-0.5 bg-white" required data-testid="product-category-select">
                        <option value="">Select...</option>
                        {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        <option value="__new__">+ Add New</option>
                      </select>
                    ) : (
                      <div className="flex gap-1 mt-0.5">
                        <Input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Name" className="h-8 text-sm flex-1" autoFocus />
                        <Input type="number" value={newCategoryGpMargin} onChange={(e) => setNewCategoryGpMargin(e.target.value)} placeholder="GP%" className="h-8 text-sm w-14" />
                        <Button type="button" onClick={handleAddCategory} size="sm" className="h-8 px-2">+</Button>
                        <Button type="button" onClick={() => { setShowNewCategory(false); setNewCategoryGpMargin("35"); }} variant="outline" size="sm" className="h-8 px-1.5">×</Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Sub-Category</Label>
                    {!showNewSubCategory ? (
                      <select value={formData.sub_category} onChange={(e) => { 
                        if (e.target.value === "__new__") { setShowNewSubCategory(true); } 
                        else { 
                          const scId = e.target.value;
                          const sc = subCategories.find(s => s.id === scId);
                          setFormData(prev => {
                            const updated = { ...prev, sub_category: scId };
                            // Only update GP% and cost for NEW products when user hasn't manually entered cost
                            if (!editingProduct && !userEnteredCost && sc?.gp_margin > 0) {
                              updated.gp_percent = sc.gp_margin.toString();
                              if (prev.retail_price) {
                                const c = calculateCostFromGp(prev.retail_price, sc.gp_margin);
                                if (c) updated.cost_price = c;
                              }
                            }
                            if (sc) {
                              if (sc.tax_1 !== undefined) updated.tax_1 = sc.tax_1;
                              if (sc.tax_2 !== undefined) updated.tax_2 = sc.tax_2;
                              if (sc.foodstamp !== undefined) updated.foodstamp = sc.foodstamp;
                            }
                            return updated;
                          });
                        } 
                      }}
                        className="w-full h-8 text-sm border border-zinc-200 rounded-md px-2 mt-0.5 bg-white" disabled={!formData.category}>
                        <option value="">Select...</option>
                        {[...filteredSubCategories].sort((a, b) => a.name.localeCompare(b.name)).map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                        {formData.category && <option value="__new__">+ Add New</option>}
                      </select>
                    ) : (
                      <div className="flex gap-1 mt-0.5">
                        <Input value={newSubCategoryName} onChange={(e) => setNewSubCategoryName(e.target.value)} placeholder="Name" className="h-8 text-sm flex-1" autoFocus />
                        <Button type="button" onClick={handleAddSubCategory} size="sm" className="h-8 px-2">+</Button>
                        <Button type="button" onClick={() => setShowNewSubCategory(false)} variant="outline" size="sm" className="h-8 px-1.5">×</Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 5: Group | MFG */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Group</Label>
                    {!showNewGroup ? (
                      <select value={formData.group} onChange={(e) => { if (e.target.value === "__new__") { setShowNewGroup(true); } else { setFormData({ ...formData, group: e.target.value }); } }}
                        className="w-full h-8 text-sm border border-zinc-200 rounded-md px-2 mt-0.5 bg-white">
                        <option value="">Select...</option>
                        {[...groups].sort((a, b) => a.name.localeCompare(b.name)).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                        <option value="__new__">+ Add New</option>
                      </select>
                    ) : (
                      <div className="flex gap-1 mt-0.5">
                        <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Name" className="h-8 text-sm flex-1" autoFocus />
                        <Button type="button" onClick={handleAddGroup} size="sm" className="h-8 px-2">+</Button>
                        <Button type="button" onClick={() => setShowNewGroup(false)} variant="outline" size="sm" className="h-8 px-1.5">×</Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Manufacturer</Label>
                    {!showNewManufacturer ? (
                      <select value={formData.mfg} onChange={(e) => { if (e.target.value === "__new__") { setShowNewManufacturer(true); } else { setFormData({ ...formData, mfg: e.target.value }); } }}
                        className="w-full h-8 text-sm border border-zinc-200 rounded-md px-2 mt-0.5 bg-white">
                        <option value="">Select...</option>
                        {[...manufacturers].sort((a, b) => a.name.localeCompare(b.name)).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        <option value="__new__">+ Add New</option>
                      </select>
                    ) : (
                      <div className="flex gap-1 mt-0.5">
                        <Input value={newManufacturerName} onChange={(e) => setNewManufacturerName(e.target.value)} placeholder="Name" className="h-8 text-sm flex-1" autoFocus />
                        <Button type="button" onClick={handleAddManufacturer} size="sm" className="h-8 px-2">+</Button>
                        <Button type="button" onClick={() => setShowNewManufacturer(false)} variant="outline" size="sm" className="h-8 px-1.5">×</Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 6: Stock | Min Stock | Fluid (oz) | Vendor */}
                <div className="grid grid-cols-6 gap-2">
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Stock</Label>
                    <Input type="number" value={formData.stock_quantity} onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                      className="h-8 text-sm mt-0.5" placeholder="0" data-testid="product-stock-input" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Min Stock</Label>
                    <Input type="number" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                      className="h-8 text-sm mt-0.5" placeholder="0" />
                  </div>
                  <div title={fluidLimitEnabled ? "Fluid ounces for this item" : "Enable Fluid Limit on this item's category to use this"}>
                    <Label htmlFor="fluid_oz" className={`text-xs font-medium ${fluidLimitEnabled ? "text-zinc-600" : "text-zinc-300"}`}>Fluid (oz)</Label>
                    <Input
                      id="fluid_oz"
                      type="number"
                      step="0.1"
                      min="0"
                      disabled={!fluidLimitEnabled}
                      value={fluidLimitEnabled ? formData.fluid_oz : ""}
                      onChange={(e) => setFormData({ ...formData, fluid_oz: e.target.value })}
                      placeholder={fluidLimitEnabled ? "e.g., 20" : "—"}
                      className="h-8 text-sm mt-0.5 disabled:bg-zinc-100 disabled:text-zinc-400 disabled:cursor-not-allowed"
                      data-testid="product-fluid-oz-input"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs font-medium text-zinc-600">Vendor</Label>
                    <select value="" onChange={(e) => { if (e.target.value === "__new__") { if (onShowVendorDialog) onShowVendorDialog(true); } else if (e.target.value && !formData.vendor_ids.includes(e.target.value)) { toggleVendor(e.target.value); } }}
                      className="w-full h-8 text-sm border border-zinc-200 rounded-md px-2 mt-0.5 bg-white">
                      <option value="">Add Vendor...</option>
                      {[...vendors].sort((a, b) => a.company_name.localeCompare(b.company_name)).map(v => (
                        <option key={v.id} value={v.id} disabled={formData.vendor_ids.includes(v.id)}>{v.company_name} {formData.vendor_ids.includes(v.id) ? '\u2713' : ''}</option>
                      ))}
                      <option value="__new__">+ Add New</option>
                    </select>
                    {formData.vendor_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {formData.vendor_ids.map(vid => {
                          const vendor = vendors.find(v => v.id === vid);
                          return vendor ? (
                            <span key={vid} className="inline-flex items-center gap-0.5 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full text-[10px]">
                              {vendor.company_name}
                              <button type="button" onClick={() => toggleVendor(vid)} className="text-red-400"><X className="w-2.5 h-2.5" /></button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 7: Image Path | Alt SKU */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Product Image</Label>
                    <div className="flex gap-1 mt-0.5">
                      <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="Path or upload..." className="h-8 text-sm flex-1" />
                      <label className="h-8 px-2 flex items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 cursor-pointer transition-colors flex-shrink-0" title="Upload image">
                        <Upload className="w-3.5 h-3.5 text-zinc-600" />
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append("file", file);
                          try {
                            const token = localStorage.getItem("token");
                            const res = await axios.post(`${API}/products/upload-image`, fd, {
                              headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
                            });
                            setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
                            toast.success("Image uploaded");
                          } catch (err) {
                            toast.error("Failed to upload image");
                          }
                          e.target.value = "";
                        }} />
                      </label>
                      {formData.image_url && (
                        <div className="h-8 w-8 rounded border border-zinc-200 overflow-hidden flex-shrink-0 bg-zinc-50 flex items-center justify-center">
                          <img src={toImageSrc(formData.image_url)} alt="" className="h-full w-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            onLoad={(e) => { e.target.style.display = 'block'; if(e.target.nextSibling) e.target.nextSibling.style.display = 'none'; }}
                          />
                          <div className="flex items-center justify-center h-full w-full" style={{display:'none'}}>
                            <ImageIcon className="w-3 h-3 text-zinc-400" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-zinc-600">Alt SKUs</Label>
                    <div className="flex gap-1 mt-0.5">
                      <Input value={newAltSku} onChange={(e) => {
                          const val = e.target.value;
                          setNewAltSku(val); setNewAltSkuError("");
                          // Debounced real-time validation
                          if (altSkuCheckTimer.current) clearTimeout(altSkuCheckTimer.current);
                          if (val.trim()) {
                            altSkuCheckTimer.current = setTimeout(async () => {
                              if (val === formData.sku) { setNewAltSkuError("Alt-SKU cannot be same as main SKU"); return; }
                              if (formData.alt_skus.includes(val)) { setNewAltSkuError("This Alt-SKU already exists"); return; }
                              const err = await validateSku(val, true, formData.alt_skus.length);
                              if (err) setNewAltSkuError(err);
                            }, 400);
                          }
                        }}
                        placeholder="Enter alt SKU" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAltSku())}
                        className={`h-8 text-sm font-mono flex-1 ${newAltSkuError ? "border-red-400" : ""}`}
                        disabled={hasSkuError && !newAltSkuError} />
                      <Button type="button" onClick={addAltSku} variant="outline" className="h-8 px-2" disabled={hasSkuError && !newAltSkuError}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    {newAltSkuError && <p className="text-red-500 text-[10px]">{newAltSkuError}</p>}
                  </div>
                </div>

                {/* Alt SKU list + Scan Data (conditional, compact) */}
                {(formData.alt_skus.length > 0 || enabledScanDataMfgs.length > 0) && (
                  <div className="grid grid-cols-2 gap-2">
                    {formData.alt_skus.length > 0 && (
                      <div className="border border-zinc-200 rounded-md overflow-hidden">
                        <div className="max-h-[80px] overflow-y-auto">
                          <table className="w-full text-xs">
                            <tbody>
                              {[...formData.alt_skus].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).map((sku, i) => (
                                <tr key={sku} className={i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}>
                                  <td className="px-2 py-0.5 font-mono">{sku}</td>
                                  <td className="px-1 py-0.5 w-6 text-center">
                                    <button type="button" onClick={() => removeAltSku(formData.alt_skus.indexOf(sku))} className="text-red-400"><X className="w-3 h-3" /></button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {/* Altria / AGDC Scan Data attributes (V6.73) — V6.82: maintained centrally (Console → Catalog), hidden on stores */}
                    <div className="hidden" data-testid="tobacco-scan-data-section">
                      <Label className="text-xs text-zinc-500">Tobacco Scan Data (Altria file)</Label>
                      <div className="grid grid-cols-5 gap-1 mt-0.5">
                        <select value={formData.sd_category} onChange={(e) => setFormData(prev => ({ ...prev, sd_category: e.target.value }))} className="h-8 px-1 border rounded-md text-xs bg-white" data-testid="sd-category-select">
                          <option value="">Not tobacco</option>
                          {["CIG", "MST", "CIGAR", "ONP", "VAPE", "OTP"].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={formData.sd_manufacturer} onChange={(e) => setFormData(prev => ({ ...prev, sd_manufacturer: e.target.value }))} className="h-8 px-1 border rounded-md text-xs bg-white" data-testid="sd-manufacturer-select">
                          <option value="">Manufacturer</option>
                          {["PM USA", "USSTC", "JMC", "Helix", "NJOY", "RJR", "ITG", "Liggett", "Swisher", "Swedish Match", "Other"].map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select value={formData.sd_uom} onChange={(e) => { const u = e.target.value; const def = { Pack: 1, Carton: 10, Can: 1, Roll: 5, Each: 1, Stick: 1, Box: 1 }[u]; setFormData(prev => ({ ...prev, sd_uom: u, sd_consumer_units: prev.sd_consumer_units === "" && def ? def : prev.sd_consumer_units })); }} className="h-8 px-1 border rounded-md text-xs bg-white" data-testid="sd-uom-select">
                          <option value="">Unit</option>
                          {["Pack", "Carton", "Can", "Roll", "Each", "Stick", "Box"].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                        <Input type="number" min="1" placeholder="Units" value={formData.sd_consumer_units} onChange={(e) => setFormData(prev => ({ ...prev, sd_consumer_units: e.target.value }))} className="h-8 text-xs" title="Consumer units per unit sold (carton=10, roll=5)" data-testid="sd-consumer-units-input" />
                        <Input placeholder="12-digit UPC" value={formData.upc} onChange={(e) => setFormData(prev => ({ ...prev, upc: e.target.value.replace(/\D/g, "") }))} maxLength={14} className="h-8 text-xs" title="Leave blank to use the SKU" data-testid="sd-upc-input" />
                      </div>
                    </div>
                    {/* ScanData Tag */}
                    <div data-testid="scan-data-tag-section">
                      <Label className="text-xs text-zinc-500" data-testid="scan-data-tag-label">ScanData Tag</Label>
                        <div className="flex gap-1">
                          <select data-testid="scan-data-tag-select" value={selectedScanDataMfg} onChange={(e) => {
                            if (e.target.value === "__new__") { setShowNewScanDataTag(true); setSelectedScanDataMfg(""); }
                            else setSelectedScanDataMfg(e.target.value);
                          }}
                            className="flex-1 h-8 px-2 border rounded-md text-xs bg-white">
                            <option value="">ScanData Tag...</option>
                            {enabledScanDataMfgs.filter(mfg => !formData.scan_data_mfg?.includes(mfg.id)).sort((a, b) => a.name.localeCompare(b.name))
                              .map(mfg => (<option key={mfg.id} value={mfg.id}>{mfg.name}</option>))}
                            <option value="__new__">+ Add New Tag...</option>
                          </select>
                          <Button type="button" variant="outline" className="h-8 px-2"
                            onClick={() => { if (selectedScanDataMfg && !formData.scan_data_mfg?.includes(selectedScanDataMfg)) { setFormData(prev => ({ ...prev, scan_data_mfg: [...(prev.scan_data_mfg || []), selectedScanDataMfg] })); setSelectedScanDataMfg(""); } }}
                            disabled={!selectedScanDataMfg}><Plus className="w-3 h-3" /></Button>
                        </div>
                        {showNewScanDataTag && (
                          <div className="flex gap-1 mt-1">
                            <Input data-testid="new-scan-data-tag-input" value={newScanDataTagName} onChange={(e) => setNewScanDataTagName(e.target.value)} placeholder="New tag name..." className="h-7 text-xs flex-1" autoFocus
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); document.getElementById('btn-save-scandata-tag')?.click(); } if (e.key === 'Escape') { setShowNewScanDataTag(false); setNewScanDataTagName(""); } }} />
                            <Button type="button" id="btn-save-scandata-tag" data-testid="save-scan-data-tag-button" variant="default" className="h-7 px-2 text-xs" disabled={!newScanDataTagName.trim()}
                              onClick={async () => {
                                try {
                                  const res = await axios.post(`${API}/scan-data/manufacturers`, { name: newScanDataTagName.trim() }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
                                  toast.success(`Tag "${res.data.name}" created`);
                                  setShowNewScanDataTag(false); setNewScanDataTagName("");
                                  // Refresh the tag list to include the new one
                                  try {
                                    const r = await axios.get(`${API}/scan-data/manufacturers`);
                                    if (onRefreshScanDataMfgs) onRefreshScanDataMfgs(r.data);
                                  } catch (refreshError) {
                                    console.error("Failed to refresh ScanData tags", refreshError);
                                  }
                                  // Auto-add to current product
                                  setFormData(prev => ({ ...prev, scan_data_mfg: [...(prev.scan_data_mfg || []), res.data.id] }));
                                } catch (err) { toast.error(getErrorMessage(err, "Failed to create tag")); }
                              }}>Save</Button>
                            <Button type="button" data-testid="cancel-scan-data-tag-button" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setShowNewScanDataTag(false); setNewScanDataTagName(""); }}>Cancel</Button>
                          </div>
                        )}
                        {formData.scan_data_mfg?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {formData.scan_data_mfg.map((mfgId) => { const mfg = enabledScanDataMfgs.find(m => m.id === mfgId); return mfg ? (
                              <span key={mfgId} className="inline-flex items-center gap-0.5 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full text-[10px]">
                                <BarChart3 className="w-2.5 h-2.5" />{mfg.name}
                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, scan_data_mfg: prev.scan_data_mfg.filter(id => id !== mfgId) }))} className="text-red-400"><X className="w-2.5 h-2.5" /></button>
                              </span>
                            ) : null; })}
                          </div>
                        )}
                      </div>
                  </div>
                )}

                </fieldset>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
                  <Button type="button" variant="outline" className="h-9 px-5" onClick={() => onOpenChange(false)}>Cancel</Button>
                  <Button type="button" className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    disabled={centralBlocked || skuError || newAltSkuError || Object.values(altSkuErrors).some(e => e)}
                    data-testid="save-duplicate-button"
                    onClick={(e) => handleSubmit(e, true)}>
                    {editingProduct ? "Update & Duplicate" : "Save & Duplicate"}
                  </Button>
                  <Button type="submit" className="h-9 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    disabled={centralBlocked || skuError || newAltSkuError || Object.values(altSkuErrors).some(e => e)} data-testid="save-product-button">
                    {editingProduct ? "Update Product" : "Save Product"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>


  );
}
